import type { Dispatch, AnyAction } from 'redux';
import { SubmissionError } from 'redux-form';

import * as ActivateAPI from 'ab-api/web/account/activate';
import * as AddExistingAPI from 'ab-api/web/account/addExisting';
import * as CreateAPI from 'ab-api/web/account/create';
import * as EditAPI from 'ab-api/web/account/edit';
import * as EditMemberAPI from 'ab-api/web/account/editMember';
import * as CSVExportAPI from 'ab-api/web/account/findAllForCsv';
import * as FindAllForOrderReportOperatingFilterAPI from 'ab-api/web/account/findAllForOrderReportOperatingFilter';
import * as FindAllForOrderReportRequestingFilterAPI from 'ab-api/web/account/findAllForOrderReportRequestingFilter';
import * as FindAllForReportTypeViewAPI from 'ab-api/web/account/findAllForReportTypeView';
import * as FindAllProjectManagersForFieldReportFilterAPI from 'ab-api/web/account/findAllProjectManagersForFieldReportFilter';
import * as FindAllSuperintendentsAndProjectManagersWithEmailAPI from 'ab-api/web/account/findAllSuperintendentsAndProjectManagersInCompanyWithEmail';
import * as FindAllSuperintendentsForFieldReportFilterAPI from 'ab-api/web/account/findAllSuperintendentsForFieldReportFilter';
import * as NotifyAllAPI from 'ab-api/web/account/findForNotifyAll';
import * as ResendEmailInviteAPI from 'ab-api/web/account/resendEmailInvite';
import * as ResendPhoneInviteAPI from 'ab-api/web/account/resendPhoneInvite';
import * as SoftDeleteAPI from 'ab-api/web/account/softDelete';
import * as UpdateDigitalSignatureAPI from 'ab-api/web/account/updateDigitalSignature';
import * as CSVUploadAPI from 'ab-api/web/account/uploadCSV';

import type { ActiveVM } from 'ab-viewModels/account/active.viewModel';
import type MemberVM from 'ab-viewModels/account/member.viewModel';
import type AccountOptionVM from 'ab-viewModels/account/option.viewModel';
import type { PreviewVM } from 'ab-viewModels/account/preview.viewModel';
import type { TableVM } from 'ab-viewModels/account/table.viewModel';
import type { CSVBulkAccountVM } from 'ab-viewModels/account/upload.viewModel';
import type { AccountViewModel } from 'ab-viewModels/account.viewModel';
import type * as User from 'ab-viewModels/user.viewModel';

import type { CSVBulkAccountRM } from 'ab-requestModels/account/upload.requestModel';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import type { ErrorOverride } from 'af-utils/actions.util';
import * as FormUtil from 'af-utils/form.util';
import { http } from 'af-utils/http.util';
import type { HttpError } from 'af-utils/http.util';

import { ACCOUNT_EDIT, MEMBER_INVITE } from 'af-constants/reduxForms';
import API from 'af-constants/routes/api';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import * as authenticationActionCreators from 'af-actions/authentication/authentication.actionCreators';
import * as companyActionCreators from 'af-actions/companies/companies.actionCreators';

/**
 * @param handler the action applied in cases of client errors
 */
function _handleAuthClientErrors(handler: (error: HttpError) => void): ErrorOverride {
	return {
		err400: handler,
		err404: handler,
	};
}

// #region CREATE

export function create(form: CreateAPI.W_Account_Create_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const options = {
				submitting: MEMBER_INVITE,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'imageUrl');

			await http.post<void>(CreateAPI.URL(), fd, options);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function addExisting(form: AddExistingAPI.W_Account_AddExisting_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post<void>(AddExistingAPI.URL(), form, { submitting: MEMBER_INVITE });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion
// #region READ

export function findById(accountId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<MemberVM>(API.V1.ACCOUNT.BY_ID(accountId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findActiveAccount() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<ActiveVM>(API.V1.ACCOUNT.ACTIVE);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForCompanyTable(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<TableVM>>(API.V1.ACCOUNT.COMPANY_TABLE(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllDeletedForCompanyTable(tableRequestModel: TableQuery = {} as TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<TableVM>>(API.V1.ACCOUNT.COMPANY_TABLE_DELETED(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForCompanyList(filterText: string, isDeleted?: boolean) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const result = await http.get<CSVExportAPI.W_Account_FindAllForCSV_VM>(CSVExportAPI.URL(filterText, isDeleted));
			return result.rows;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForCompanyPreview() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<PreviewVM[]>(API.V1.ACCOUNT.ACCOUNTS_FOR_COMPANY);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForReportTypeView() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllForReportTypeViewAPI.W_Account_FindAllForReportTypeView_VM>(FindAllForReportTypeViewAPI.URL());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForNotifyAll() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const result = await http.get<NotifyAllAPI.W_Account_FindForNotifyAll_VM>(NotifyAllAPI.URL());
			return result.rows;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllAssignableForDeliverable() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<AccountViewModel[]>(API.V1.ACCOUNT.FIND_FOR_DELIVERABLE);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllAssignableAsAccounting() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<AccountOptionVM[]>(API.V1.ACCOUNT.FIND_ACCOUNTING_OPTIONS);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllAssignableAsTechnician() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<AccountOptionVM[]>(API.V1.ACCOUNT.FIND_TECHNICIAN_OPTIONS);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllAssignableAsManagement() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<AccountOptionVM[]>(API.V1.ACCOUNT.FIND_MANAGEMENT_OPTIONS);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSuperintendentsForFieldReportFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllSuperintendentsForFieldReportFilterAPI.W_Account_FindAllSuperintendentsForFieldReportFilter_VM>(
				FindAllSuperintendentsForFieldReportFilterAPI.URL()
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForOrderReportOperatorFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllForOrderReportOperatingFilterAPI.W_Account_FindAllForOrderReportOperatingFilter_VM>(
				FindAllForOrderReportOperatingFilterAPI.URL()
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllProjectManagersForFieldReportFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllProjectManagersForFieldReportFilterAPI.W_Account_FindAllProjectManagersForFieldReportFilter_VM>(
				FindAllProjectManagersForFieldReportFilterAPI.URL()
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForOrderReportRequestingFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllForOrderReportRequestingFilterAPI.W_Account_FindAllForOrderReportRequestingFilter_VM>(
				FindAllForOrderReportRequestingFilterAPI.URL()
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findSuperintendentsAndProjectManagersWithEmail() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllSuperintendentsAndProjectManagersWithEmailAPI.W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM>(
				FindAllSuperintendentsAndProjectManagersWithEmailAPI.URL()
			);

		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion
// #region UPDATE

export function activate(accountId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put<void>(ActivateAPI.URL(accountId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function resendEmailInvite(accountId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post<void>(ResendEmailInviteAPI.URL(accountId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function resendPhoneInvite(accountId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post<void>(ResendPhoneInviteAPI.URL(accountId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function update(form: EditMemberAPI.W_Account_EditMember_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			if (!form.id) {
				throw new Error('Account ID not provided');
			}

			const options = {
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'imageUrl');
			await http.put<void>(EditMemberAPI.URL(form.id), fd, options);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function updateActiveAccount(form: EditAPI.W_Account_Edit_RM) {
	return async (
		dispatch: Dispatch<authenticationActionCreators.AuthenticationAction>,
		getState: GetRootState, { redirectTo }
	) => {
		const action = async () => {

			const options = {
				submitting: ACCOUNT_EDIT,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'image');
			const user = await http.put<EditAPI.W_Account_Edit_VM>(EditAPI.URL(), fd, options);
			const userData = {
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
				phoneNumber: user.phoneNumber,
				countryCode: user.countryCode,
				isDigitalSignatureEnabled: user.isDigitalSignatureEnabled,
				digitalSignatureUrl: user.digitalSignatureUrl,
				digitalSignatureId: user.digitalSignatureId,
			} as User.UserData;
			dispatch(authenticationActionCreators.UPDATE_USER(userData));

			return user;
		};
		const error: ErrorOverride = {
			..._handleAuthClientErrors((errorData) => {
				if (errorData?.response?.data) {
					throw new SubmissionError(errorData.response.data);
				} else {
					throw new SubmissionError({ _error: 'Edit user error' });
				}
			}),
		};
		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

export function saveDigitalSignature(form: UpdateDigitalSignatureAPI.W_Account_UpdateDigitalSignature_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const signature = await http.patch<UpdateDigitalSignatureAPI.W_Account_UpdateDigitalSignature_VM>(UpdateDigitalSignatureAPI.URL(), form);
			const userData = {
				isDigitalSignatureEnabled: signature.isDigitalSignatureEnabled,
				digitalSignatureUrl: signature.digitalSignatureUrl,
				digitalSignatureId: signature.digitalSignatureId,
				showCreateDigitalSignature: signature.showCreateDigitalSignature,
			} as User.UserData;
			dispatch(authenticationActionCreators.UPDATE_USER(userData));
			return signature;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion
// #region DELETE

export function remove(accountId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.delete<void>(SoftDeleteAPI.URL(accountId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion

// #region CSV IMPORT

export function validateCSV(members: CSVBulkAccountRM['accounts']) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {

			const response = await http.post<CSVBulkAccountVM>(API.V1.ACCOUNT.CSV_VALIDATE, members);

			dispatch(companyActionCreators.SET_ACCEPTED_MEMBERS(response.valid));
			dispatch(companyActionCreators.SET_REJECTED_MEMBERS(response.invalid));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function upload(form: CSVUploadAPI.W_Account_UploadCSV_RM) {
	return async (dispatch: Dispatch<companyActionCreators.CompanyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post<void>(CSVUploadAPI.URL(), form);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion
